import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Grid,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Pagination,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import IconButton from "@mui/material/IconButton";
import { API_URL } from "./config";

import {
  getCampusFromToken,
  getAuthHeaders,
} from "./commonfunctions/useAuthHelpers";

const WalletBonusRulesPage = () => {
  // ✅ Theme constants
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const pageWrapSx = { p: 2, backgroundColor: BG, minHeight: "100vh" };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };
  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };
  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: BORDER_FOCUS,
    },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const iconBtnSx = {
    borderRadius: 2,
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      backgroundColor: "rgba(240,120,40,0.10)",
      color: THEME_ORANGE,
    },
  };

  const tableWrapSx = {
    ...surfaceSx,
    mt: 1,
    overflow: "hidden",
  };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
    },
  };

  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)" },
  });

  const emptyForm = {
    subCampusCode: "",
    ruleCode: "",
    ruleName: "",
    bonusType: "FIXED",
    bonusValue: "",
    minRechargeAmount: "",
    maxBonusAmount: "",
    validFrom: "",
    validTo: "",
    maxUsesPerWallet: "",
    maxTotalUses: "",
    appliesToCardType: "ALL",
    isStackable: 0,
    isActive: 1,
  };

  const [campusCode] = useState(getCampusFromToken());
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [options, setOptions] = useState({
  SubCampusCode: [],
  });
  const [loadingLookups, setLoadingLookups] = useState(false);
  const authHeader = { headers: getAuthHeaders() };

  const [filters, setFilters] = useState({
    subCampusCode: "",
    appliesToCardType: "",
    isActive: "",
    page: 1,
    pageSize: 10,
  });

  const [rules, setRules] = useState([]);
  const [editRuleId, setEditRuleId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [total, setTotal] = useState(0);

  const formatLocalDateTime = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleString();
  };

  const toLocalInputValue = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const tzOffsetMs = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tzOffsetMs).toISOString().slice(0, 16);
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  };

  const onFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((s) => ({ ...s, [name]: value }));
  };

  const normalizePayload = (src) => ({
    subCampusCode: src.subCampusCode || src.SubCampusCode || "",
    ruleCode: src.ruleCode || src.RuleCode || "",
    ruleName: src.ruleName || src.RuleName || "",
    bonusType: src.bonusType || src.BonusType || "FIXED",
    bonusValue:
      src.bonusValue !== undefined && src.bonusValue !== null
        ? src.bonusValue
        : src.BonusValue !== undefined && src.BonusValue !== null
        ? src.BonusValue
        : "",
    minRechargeAmount:
      src.minRechargeAmount !== undefined && src.minRechargeAmount !== null
        ? src.minRechargeAmount
        : src.MinRechargeAmount !== undefined && src.MinRechargeAmount !== null
        ? src.MinRechargeAmount
        : "",
    maxBonusAmount:
      src.maxBonusAmount !== undefined && src.maxBonusAmount !== null
        ? src.maxBonusAmount
        : src.MaxBonusAmount !== undefined && src.MaxBonusAmount !== null
        ? src.MaxBonusAmount
        : "",
    validFrom: src.validFrom || src.ValidFrom || "",
    validTo: src.validTo || src.ValidTo || "",
    maxUsesPerWallet:
      src.maxUsesPerWallet !== undefined && src.maxUsesPerWallet !== null
        ? src.maxUsesPerWallet
        : src.MaxUsesPerWallet !== undefined && src.MaxUsesPerWallet !== null
        ? src.MaxUsesPerWallet
        : "",
    maxTotalUses:
      src.maxTotalUses !== undefined && src.maxTotalUses !== null
        ? src.maxTotalUses
        : src.MaxTotalUses !== undefined && src.MaxTotalUses !== null
        ? src.MaxTotalUses
        : "",
    appliesToCardType: src.appliesToCardType || src.AppliesToCardType || "ALL",
    isStackable:
      src.isStackable !== undefined && src.isStackable !== null
        ? Number(src.isStackable)
        : src.IsStackable !== undefined && src.IsStackable !== null
        ? Number(src.IsStackable)
        : 0,
    isActive:
      src.isActive !== undefined && src.isActive !== null
        ? Number(src.isActive)
        : src.IsActive !== undefined && src.IsActive !== null
        ? Number(src.IsActive)
        : 1,
  });

  const fetchRules = async (overrideFilters = null) => {
    try {
      const activeFilters = overrideFilters || filters;
      const res = await axios.get(`${API_URL}/api/wallet/admin/bonus-rules`, {
        headers: getAuthHeaders(),
        params: activeFilters,
      });
      setRules(res.data?.rows || []);
      setTotal(Number(res.data?.total || 0));
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load bonus rules");
    }
  };

  useEffect(() => {
    if (!campusCode) return;
    let cancelled = false;
  
    (async () => {
      try {
        setLoadingLookups(true);
  
        const { data } = await axios.get(
          `${API_URL}/api/lookups/all`,
          {
            headers: getAuthHeaders(),
            params: { CampusCode: campusCode },
          }
        );
  
        if (cancelled) return;
  
        setOptions({
          SubCampusCode: data?.SubCampusCode || [],
        });
        
      } catch (e) {
        console.error(e);
        setError("Failed to load dropdowns");
      } finally {
        if (!cancelled) setLoadingLookups(false);
      }
    })();
  
    return () => {
      cancelled = true;
    };
  }, [campusCode]);

  useEffect(() => {
    fetchRules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveRule = async () => {
    try {
      await axios.post(
        `${API_URL}/api/wallet/admin/bonus-rules`,
        {
          ...form,
          bonusValue: Number(form.bonusValue || 0),
          minRechargeAmount: form.minRechargeAmount ? Number(form.minRechargeAmount) : null,
          maxBonusAmount: form.maxBonusAmount ? Number(form.maxBonusAmount) : null,
          maxUsesPerWallet: form.maxUsesPerWallet ? Number(form.maxUsesPerWallet) : null,
          maxTotalUses: form.maxTotalUses ? Number(form.maxTotalUses) : null,
          isStackable: Number(form.isStackable || 0),
          isActive: Number(form.isActive || 0),
        },
        { headers: getAuthHeaders() }
      );

      setSuccess("Bonus rule saved successfully");
      setForm(emptyForm);
      await fetchRules({ ...filters, page: 1 });
      setFilters((s) => ({ ...s, page: 1 }));
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to save bonus rule");
    }
  };

  const handleEdit = (row) => {
    setEditRuleId(row.BonusRuleId);
    setEditForm(normalizePayload(row));
  };

  const cancelEdit = () => {
    setEditRuleId(null);
    setEditForm({});
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((s) => ({ ...s, [name]: value }));
  };

  const updateRule = async (bonusRuleId) => {
    try {
      await axios.put(
        `${API_URL}/api/wallet/admin/bonus-rules/${bonusRuleId}`,
        {
          ...editForm,
          bonusValue: Number(editForm.bonusValue || 0),
          minRechargeAmount: editForm.minRechargeAmount ? Number(editForm.minRechargeAmount) : null,
          maxBonusAmount: editForm.maxBonusAmount ? Number(editForm.maxBonusAmount) : null,
          maxUsesPerWallet: editForm.maxUsesPerWallet ? Number(editForm.maxUsesPerWallet) : null,
          maxTotalUses: editForm.maxTotalUses ? Number(editForm.maxTotalUses) : null,
          isStackable: Number(editForm.isStackable || 0),
          isActive: Number(editForm.isActive || 0),
        },
        { headers: getAuthHeaders() }
      );

      setSuccess("Bonus rule updated successfully");
      setEditRuleId(null);
      setEditForm({});
      await fetchRules();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to update bonus rule");
    }
  };

  const applyFilters = async () => {
    const next = { ...filters, page: 1 };
    setFilters(next);
    await fetchRules(next);
  };

  const resetFilters = async () => {
    const next = {
      subCampusCode: "",
      appliesToCardType: "",
      isActive: "",
      page: 1,
      pageSize: 10,
    };
    setFilters(next);
    await fetchRules(next);
  };

  const handlePageChange = async (_e, value) => {
    const next = { ...filters, page: value };
    setFilters(next);
    await fetchRules(next);
  };

  const handlePageSizeChange = async (e) => {
    const next = {
      ...filters,
      pageSize: Number(e.target.value),
      page: 1,
    };
    setFilters(next);
    await fetchRules(next);
  };

  const totalPages = Math.max(1, Math.ceil(total / filters.pageSize));

  const pageText = useMemo(() => {
    if (!total) return "0 records";
    const from = (filters.page - 1) * filters.pageSize + 1;
    const to = Math.min(filters.page * filters.pageSize, total);
    return `${from}-${to} of ${total}`;
  }, [filters.page, filters.pageSize, total]);

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900 }}>
              Wallet Bonus Rules
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Configure, edit, filter, activate, and inactivate recharge bonus campaigns
            </Typography>
          </Box>
        </Box>

        <Box sx={bodySx}>
          {!!error && (
            <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!!success && (
            <Snackbar open autoHideDuration={3000} onClose={() => setSuccess("")}>
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                {success}
              </Alert>
            </Snackbar>
          )}

          <Paper elevation={0} sx={{ ...surfaceSx, p: 2, mb: 2 }}>
            <Typography sx={{ fontWeight: 900, mb: 2 }}>
              Create Bonus Rule
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  name="subCampusCode"
                  label="SubCampus"
                  value={form.subCampusCode}
                  onChange={onChange}
                  fullWidth
                  size="small"
                  sx={inputSx}
                  disabled={loadingLookups}
                >
                  <MenuItem value="">Select</MenuItem>
                  {options.SubCampusCode.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  name="ruleCode"
                  label="Rule Code"
                  value={form.ruleCode}
                  onChange={onChange}
                  fullWidth
                  size="small"
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  name="ruleName"
                  label="Rule Name"
                  value={form.ruleName}
                  onChange={onChange}
                  fullWidth
                  size="small"
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <TextField
                  select
                  name="bonusType"
                  label="Bonus Type"
                  value={form.bonusType}
                  onChange={onChange}
                  fullWidth
                  size="small"
                  sx={inputSx}
                >
                  <MenuItem value="FIXED">FIXED</MenuItem>
                  <MenuItem value="PERCENT">PERCENT</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={2}>
                <TextField
                  name="bonusValue"
                  label="Bonus Value"
                  type="number"
                  value={form.bonusValue}
                  onChange={onChange}
                  fullWidth
                  size="small"
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <TextField
                  name="minRechargeAmount"
                  label="Min Recharge Amount"
                  type="number"
                  value={form.minRechargeAmount}
                  onChange={onChange}
                  fullWidth
                  size="small"
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <TextField
                  name="maxBonusAmount"
                  label="Max Bonus Amount"
                  type="number"
                  value={form.maxBonusAmount}
                  onChange={onChange}
                  fullWidth
                  size="small"
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <TextField
                  name="validFrom"
                  label="Valid From"
                  type="datetime-local"
                  value={form.validFrom}
                  onChange={onChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <TextField
                  name="validTo"
                  label="Valid To"
                  type="datetime-local"
                  value={form.validTo}
                  onChange={onChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <TextField
                  name="maxUsesPerWallet"
                  label="Max Uses Per Wallet"
                  type="number"
                  value={form.maxUsesPerWallet}
                  onChange={onChange}
                  fullWidth
                  size="small"
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <TextField
                  name="maxTotalUses"
                  label="Max Total Uses"
                  type="number"
                  value={form.maxTotalUses}
                  onChange={onChange}
                  fullWidth
                  size="small"
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <TextField
                  select
                  name="appliesToCardType"
                  label="Applies To"
                  value={form.appliesToCardType}
                  onChange={onChange}
                  fullWidth
                  size="small"
                  sx={inputSx}
                >
                  <MenuItem value="ALL">ALL</MenuItem>
                  <MenuItem value="STUDENT">STUDENT</MenuItem>
                  <MenuItem value="EMPLOYEE">EMPLOYEE</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={1.5}>
                <TextField
                  select
                  name="isStackable"
                  label="Stackable"
                  value={form.isStackable}
                  onChange={onChange}
                  fullWidth
                  size="small"
                  sx={inputSx}
                >
                  <MenuItem value={0}>No</MenuItem>
                  <MenuItem value={1}>Yes</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={1.5}>
                <TextField
                  select
                  name="isActive"
                  label="Active"
                  value={form.isActive}
                  onChange={onChange}
                  fullWidth
                  size="small"
                  sx={inputSx}
                >
                  <MenuItem value={1}>Yes</MenuItem>
                  <MenuItem value={0}>No</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={2}>
                <Button
                  variant="contained"
                  sx={{ ...primaryBtnSx, mt: { xs: 0, md: "2px" }, height: "40px" }}
                  onClick={saveRule}
                  fullWidth
                >
                  Save Rule
                </Button>
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={0} sx={{ ...surfaceSx, p: 2, mb: 2 }}>
            <Typography sx={{ fontWeight: 900, mb: 2 }}>
              Filter Existing Rules
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
               <TextField
                 select
                 name="subCampusCode"
                 label="SubCampus"
                 value={filters.subCampusCode}
                 onChange={onFilterChange}
                 fullWidth
                 size="small"
                 sx={inputSx}
                 disabled={loadingLookups}
               >
                 <MenuItem value="">All</MenuItem>
                 {options.SubCampusCode.map((opt) => (
                   <MenuItem key={opt} value={opt}>
                     {opt}
                   </MenuItem>
                 ))}
               </TextField>
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  select
                  name="appliesToCardType"
                  label="Applies To"
                  value={filters.appliesToCardType}
                  onChange={onFilterChange}
                  fullWidth
                  size="small"
                  sx={inputSx}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="ALL">ALL</MenuItem>
                  <MenuItem value="STUDENT">STUDENT</MenuItem>
                  <MenuItem value="EMPLOYEE">EMPLOYEE</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={2}>
                <TextField
                  select
                  name="isActive"
                  label="Active"
                  value={filters.isActive}
                  onChange={onFilterChange}
                  fullWidth
                  size="small"
                  sx={inputSx}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="1">Active</MenuItem>
                  <MenuItem value="0">Inactive</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={2}>
                <TextField
                  select
                  name="pageSize"
                  label="Rows"
                  value={filters.pageSize}
                  onChange={handlePageSizeChange}
                  fullWidth
                  size="small"
                  sx={inputSx}
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={2}>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    startIcon={<SearchIcon />}
                    sx={primaryBtnSx}
                    onClick={applyFilters}
                    fullWidth
                  >
                    Apply
                  </Button>

                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    sx={outlinedBtnSx}
                    onClick={resetFilters}
                    fullWidth
                  >
                    Reset
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Paper>

           <Paper elevation={0} sx={tableWrapSx}>
             <Box
               sx={{
                 px: 1.5,
                 py: 1.25,
                 borderBottom: "1px solid rgba(25,118,210,0.12)",
                 display: "flex",
                 alignItems: "center",
                 justifyContent: "space-between",
                 gap: 1,
                 flexWrap: "wrap",
                 backgroundColor: "#fff",
               }}
             >
               <Typography sx={{ fontWeight: 900 }}>
                 Existing Rules ({pageText})
               </Typography>
           
               <Stack direction="row" spacing={1} alignItems="center">
                 <Pagination
                   count={totalPages}
                   page={filters.page}
                   onChange={handlePageChange}
                   color="primary"
                   shape="rounded"
                   size="small"
                 />
               </Stack>
             </Box>
           
             <Box
               sx={{
                 overflowX: "auto",
                 overflowY: "hidden",
               }}
             >
               <TableContainer component="div">
                 <Table size="small">
                   <colgroup>
                     <col style={{ width: 60 }} />
                     <col style={{ width: 140 }} />
                     <col style={{ width: 220 }} />
                     <col style={{ width: 120 }} />
                     <col style={{ width: 100 }} />
                     <col style={{ width: 100 }} />
                     <col style={{ width: 130 }} />
                     <col style={{ width: 120 }} />
                     <col style={{ width: 120 }} />
                     <col style={{ width: 100 }} />
                     <col style={{ width: 90 }} />
                     <col style={{ width: 170 }} />
                     <col style={{ width: 170 }} />
                     <col style={{ width: 170 }} />
                   </colgroup>
           
                   <TableHead>
                     <TableRow sx={theadRowSx}>
                       <TableCell>#</TableCell>
                       <TableCell>Rule Code</TableCell>
                       <TableCell>Rule Name</TableCell>
                       <TableCell>SubCampus</TableCell>
                       <TableCell>Type</TableCell>
                       <TableCell>Value</TableCell>
                       <TableCell>Min Recharge</TableCell>
                       <TableCell>Max Bonus</TableCell>
                       <TableCell>Applies To</TableCell>
                       <TableCell>Stackable</TableCell>
                       <TableCell>Active</TableCell>
                       <TableCell>Valid From</TableCell>
                       <TableCell>Valid To</TableCell>
                       <TableCell>Actions</TableCell>
                     </TableRow>
                   </TableHead>
           
                   <TableBody>
                     {rules.map((row, idx) => {
                       const isEdit = editRuleId === row.BonusRuleId;
                       const rowNo = (filters.page - 1) * filters.pageSize + idx + 1;
           
                       if (isEdit) {
                         return (
                           <TableRow key={row.BonusRuleId} sx={zebraRowSx(idx)}>
                             <TableCell>{rowNo}</TableCell>
                             <TableCell>{row.RuleCode}</TableCell>
           
                             <TableCell>
                               <TextField
                                 name="ruleName"
                                 value={editForm.ruleName}
                                 onChange={handleEditChange}
                                 size="small"
                                 fullWidth
                                 sx={inputSx}
                               />
                             </TableCell>
           
                             <TableCell>
                               <TextField
                                 select
                                 name="subCampusCode"
                                 value={editForm.subCampusCode}
                                 onChange={handleEditChange}
                                 size="small"
                                 fullWidth
                                 sx={inputSx}
                               >
                                 <MenuItem value="">Select</MenuItem>
                                 {options.SubCampusCode.map((opt) => (
                                   <MenuItem key={opt} value={opt}>
                                     {opt}
                                   </MenuItem>
                                 ))}
                               </TextField>
                             </TableCell>
           
                             <TableCell>
                               <TextField
                                 select
                                 name="bonusType"
                                 value={editForm.bonusType}
                                 onChange={handleEditChange}
                                 size="small"
                                 fullWidth
                                 sx={inputSx}
                               >
                                 <MenuItem value="FIXED">FIXED</MenuItem>
                                 <MenuItem value="PERCENT">PERCENT</MenuItem>
                               </TextField>
                             </TableCell>
           
                             <TableCell>
                               <TextField
                                 name="bonusValue"
                                 type="number"
                                 value={editForm.bonusValue}
                                 onChange={handleEditChange}
                                 size="small"
                                 fullWidth
                                 sx={inputSx}
                               />
                             </TableCell>
           
                             <TableCell>
                               <TextField
                                 name="minRechargeAmount"
                                 type="number"
                                 value={editForm.minRechargeAmount}
                                 onChange={handleEditChange}
                                 size="small"
                                 fullWidth
                                 sx={inputSx}
                               />
                             </TableCell>
           
                             <TableCell>
                               <TextField
                                 name="maxBonusAmount"
                                 type="number"
                                 value={editForm.maxBonusAmount}
                                 onChange={handleEditChange}
                                 size="small"
                                 fullWidth
                                 sx={inputSx}
                               />
                             </TableCell>
           
                             <TableCell>
                               <TextField
                                 select
                                 name="appliesToCardType"
                                 value={editForm.appliesToCardType}
                                 onChange={handleEditChange}
                                 size="small"
                                 fullWidth
                                 sx={inputSx}
                               >
                                 <MenuItem value="ALL">ALL</MenuItem>
                                 <MenuItem value="STUDENT">STUDENT</MenuItem>
                                 <MenuItem value="EMPLOYEE">EMPLOYEE</MenuItem>
                               </TextField>
                             </TableCell>
           
                             <TableCell>
                               <TextField
                                 select
                                 name="isStackable"
                                 value={editForm.isStackable}
                                 onChange={handleEditChange}
                                 size="small"
                                 fullWidth
                                 sx={inputSx}
                               >
                                 <MenuItem value={0}>No</MenuItem>
                                 <MenuItem value={1}>Yes</MenuItem>
                               </TextField>
                             </TableCell>
           
                             <TableCell>
                               <TextField
                                 select
                                 name="isActive"
                                 value={editForm.isActive}
                                 onChange={handleEditChange}
                                 size="small"
                                 fullWidth
                                 sx={inputSx}
                               >
                                 <MenuItem value={1}>Yes</MenuItem>
                                 <MenuItem value={0}>No</MenuItem>
                               </TextField>
                             </TableCell>
           
                             <TableCell>
                               <TextField
                                 name="validFrom"
                                 type="datetime-local"
                                 value={toLocalInputValue(editForm.validFrom)}
                                 onChange={handleEditChange}
                                 size="small"
                                 fullWidth
                                 sx={inputSx}
                                 InputLabelProps={{ shrink: true }}
                               />
                             </TableCell>
           
                             <TableCell>
                               <TextField
                                 name="validTo"
                                 type="datetime-local"
                                 value={toLocalInputValue(editForm.validTo)}
                                 onChange={handleEditChange}
                                 size="small"
                                 fullWidth
                                 sx={inputSx}
                                 InputLabelProps={{ shrink: true }}
                               />
                             </TableCell>
           
                             <TableCell>
                               <IconButton
                                 sx={iconBtnSx}
                                 onClick={() => updateRule(row.BonusRuleId)}
                                 title="Save"
                               >
                                 <SaveIcon />
                               </IconButton>
                               <IconButton
                                 sx={iconBtnSx}
                                 onClick={cancelEdit}
                                 title="Cancel"
                               >
                                 <CancelIcon />
                               </IconButton>
                             </TableCell>
                           </TableRow>
                         );
                       }
           
                       return (
                         <TableRow key={row.BonusRuleId} sx={zebraRowSx(idx)}>
                           <TableCell>{rowNo}</TableCell>
                           <TableCell>{row.RuleCode}</TableCell>
                           <TableCell>{row.RuleName}</TableCell>
                           <TableCell>{row.SubCampusCode || "-"}</TableCell>
                           <TableCell>{row.BonusType}</TableCell>
                           <TableCell>{row.BonusValue}</TableCell>
                           <TableCell>{row.MinRechargeAmount ?? "-"}</TableCell>
                           <TableCell>{row.MaxBonusAmount ?? "-"}</TableCell>
                           <TableCell>{row.AppliesToCardType}</TableCell>
                           <TableCell>{Number(row.IsStackable) ? "Yes" : "No"}</TableCell>
                           <TableCell>{Number(row.IsActive) ? "Yes" : "No"}</TableCell>
                           <TableCell>{formatLocalDateTime(row.ValidFrom)}</TableCell>
                           <TableCell>{formatLocalDateTime(row.ValidTo)}</TableCell>
                           <TableCell>
                             <IconButton
                               sx={iconBtnSx}
                               onClick={() => handleEdit(row)}
                               title="Edit"
                             >
                               <EditIcon />
                             </IconButton>
                           </TableCell>
                         </TableRow>
                       );
                     })}
           
                     {!rules.length && (
                       <TableRow sx={zebraRowSx(0)}>
                         <TableCell colSpan={14} align="center" sx={{ py: 3 }}>
                           No bonus rules found
                         </TableCell>
                       </TableRow>
                     )}
                   </TableBody>
                 </Table>
               </TableContainer>
             </Box>
           
             <Box
               sx={{
                 px: 1.5,
                 py: 1.25,
                 borderTop: "1px solid rgba(25,118,210,0.12)",
                 display: "flex",
                 alignItems: "center",
                 justifyContent: "space-between",
                 gap: 1,
                 flexWrap: "wrap",
                 backgroundColor: "#fff",
               }}
             >
               <Typography sx={{ fontWeight: 700 }}>
                 {pageText}
               </Typography>
           
               <Pagination
                 count={totalPages}
                 page={filters.page}
                 onChange={handlePageChange}
                 color="primary"
                 shape="rounded"
                 size="small"
               />
             </Box>
           </Paper>
        </Box>
      </Paper>
    </Box>
  );
};

export default WalletBonusRulesPage;